// Sprint 2.6.5 — Sentry admin-only com beforeSend redactor.
//
// Defesa em camada:
// - Carregado APENAS em web-admin e web-comite (NÃO em web-intake — anonimato
//   do denunciante é incompatível com qualquer error tracking).
// - beforeSend redactor agressivo: descarta event se contém qualquer chave/header
//   identificador de denunciante ou material criptográfico (ciphertext, dek, jwk).
// - Sem DSN configurado (`VITE_SENTRY_DSN` ausente ou vazio) = no-op silencioso.
// - SDK `@sentry/browser` é dynamic import com `@vite-ignore` — sem dep instalada
//   no momento do commit, build não quebra. Quando Vergueiro ativar Sentry:
//   1) pnpm --filter @aions/web-admin add @sentry/browser
//   2) pnpm --filter @aions/web-comite add @sentry/browser
//   3) setar VITE_SENTRY_DSN em apps/web-{admin,comite}/.env.production
//   4) rebuild + deploy.
//
// Sampling:
// - 100% errors (admin é low-volume; volume errado = bug grave)
// - 0% transactions (não usamos performance traces em admin)
// - 0% replay (nunca; capturaria UI com PII de denunciante decifrado)
//
// Hardcoded `tracesSampleRate: 0` + `replaysOnErrorSampleRate: 0` para garantir
// que qualquer config drift não habilite acidentalmente performance/replay.

/**
 * Padrões PII que disparam descarte de event inteiro. Comparados case-insensitive
 * contra qualquer string serializada no payload do event.
 */
const PII_PATTERNS_DROP_EVENT: ReadonlyArray<RegExp> = [
  /\bipv4\b|\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/i, // IPv4 literal
  /cf-connecting-ip/i,
  /x-forwarded-for/i,
  /x-real-ip/i,
  /true-client-ip/i,
];

/**
 * Chaves de payload cujo valor é redigido (substituído por `'[REDACTED]'`) —
 * em vez de descartar o event inteiro. Preserva contexto técnico do erro
 * sem vazar material criptográfico.
 */
const REDACT_KEYS: ReadonlyArray<RegExp> = [
  /ciphertext/i,
  /\bcleartext\b/i,
  /\bplaintext\b/i,
  /^dek/i,
  /^kek/i,
  /^jwk/i,
  /privateKey/i,
  /password/i,
  /\btotp/i,
  /share/i, // Shamir share material
  /nonce/i, // cripto nonce em error context
  /referer/i,
  /user-?agent/i,
];

/**
 * Headers cujo valor é dropado inteiro do event (não substituído).
 * Aplicado em event.request.headers + event.contexts.request.headers.
 */
const DROP_HEADERS: ReadonlyArray<string> = [
  'authorization',
  'cookie',
  'x-bootstrap-secret',
  'x-biometric-hash',
  'x-session-refresh',
  'cf-connecting-ip',
  'x-forwarded-for',
  'x-real-ip',
  'true-client-ip',
  'referer',
  'user-agent',
];

interface SentryEventLike {
  request?: { headers?: Record<string, unknown>; cookies?: unknown; data?: unknown };
  contexts?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  tags?: Record<string, unknown>;
  message?: unknown;
  exception?: { values?: Array<{ value?: string; stacktrace?: unknown }> };
}

interface SentryHintLike {
  originalException?: unknown;
}

/** Walk recursive: aplica redact em qualquer chave que match REDACT_KEYS. */
function redactObject<T>(obj: T, depth = 0): T {
  if (depth > 8 || obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map((v) => redactObject(v, depth + 1)) as unknown as T;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (REDACT_KEYS.some((re) => re.test(k))) {
      out[k] = '[REDACTED]';
    } else {
      out[k] = redactObject(v, depth + 1);
    }
  }
  return out as unknown as T;
}

/** Decide se descartamos o event inteiro por PII grave detectada. */
function shouldDropEvent(event: SentryEventLike): boolean {
  const serialized = JSON.stringify(event);
  return PII_PATTERNS_DROP_EVENT.some((re) => re.test(serialized));
}

/** beforeSend hook do Sentry SDK. Retorna `null` para descartar event. */
function beforeSend(event: SentryEventLike, _hint: SentryHintLike): SentryEventLike | null {
  if (shouldDropEvent(event)) return null;

  // Drop headers identificadores (não redact — drop completo).
  if (event.request?.headers) {
    const headers: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(event.request.headers)) {
      if (!DROP_HEADERS.includes(k.toLowerCase())) headers[k] = v;
    }
    event.request.headers = headers;
  }
  // Cookies sempre drop (sessão tem JWT).
  if (event.request) delete event.request.cookies;

  // Redact body do request (pode conter ciphertext de qualquer endpoint).
  if (event.request?.data) {
    event.request.data = redactObject(event.request.data);
  }

  // Redact extras + contexts + tags.
  if (event.extra) event.extra = redactObject(event.extra);
  if (event.contexts) event.contexts = redactObject(event.contexts);
  if (event.tags) event.tags = redactObject(event.tags);

  return event;
}

/**
 * Inicializa Sentry SDK quando VITE_SENTRY_DSN está setado.
 * Chamada idempotente — segura para invocar várias vezes (init interno do SDK
 * já é noop em re-init com mesma DSN).
 */
export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn || typeof dsn !== 'string' || dsn.length === 0) return;

  try {
    // Dependência opcional — instalar via `pnpm --filter @aions/web-admin add
    // @sentry/browser` (idem comite) quando Vergueiro ativar Sentry.
    // String literal partida em variável de runtime evita Rollup/Vite tentarem
    // resolver no build; sem a dep, runtime `Function('return import(...)')`
    // falha e o catch silencia.
    const sdkSpecifier = ['@sentry', 'browser'].join('/');
    const dynImport = new Function('s', 'return import(s)') as (s: string) => Promise<unknown>;
    const Sentry = (await dynImport(sdkSpecifier)) as { init: (cfg: unknown) => void };
    Sentry.init({
      dsn,
      environment: (import.meta.env.MODE as string | undefined) ?? 'production',
      release: (import.meta.env.VITE_RELEASE as string | undefined) ?? 'unknown',
      tracesSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      replaysSessionSampleRate: 0,
      autoSessionTracking: false,
      sendDefaultPii: false,
      beforeSend,
      // Allowlist por origem — só captura erros do próprio SPA, não de extensões
      // ou injeções de browser. Pares com CSP per-SPA (Plan 07-05 D-178).
      allowUrls: [/aionscorp\.com\.br/],
    });
  } catch (err) {
    // SDK ausente OU init falhou — silent no-op. NÃO logamos erro pois console
    // em prod é parte do bundle público; falha de inicialização não é actionable.
    void err;
  }
}
